import React, { useEffect, useState } from "react";
import {
  TikTokReconnectRequiredError,
  getTikTokCreatorInfo,
  type TikTokCreatorInfo,
} from "../services/tiktokAuthentication";

interface Props {
  customerId: string;
  idToken: string;
  profileId: string;
  /** Name stored at connect time; shown until (or instead of) the live info. */
  displayName?: string;
  /** Called with true on 409 reconnect_required, false once info loads. */
  onReconnectNeeded: (needed: boolean) => void;
}

/**
 * The connected TikTok account on the Connect card. The avatar URL is a
 * short-lived TikTok CDN link, so it is fetched on render and never stored.
 */
const TikTokCardStatus: React.FC<Props> = ({
  customerId,
  idToken,
  profileId,
  displayName,
  onReconnectNeeded,
}) => {
  const [info, setInfo] = useState<TikTokCreatorInfo | null>(null);
  const [reconnect, setReconnect] = useState(false);

  useEffect(() => {
    let active = true;
    setInfo(null);
    setReconnect(false);
    // Don't let a previous brand's expired flag leak into this one.
    onReconnectNeeded(false);
    getTikTokCreatorInfo(customerId, profileId, idToken)
      .then((res) => {
        if (!active) return;
        setInfo(res);
        onReconnectNeeded(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof TikTokReconnectRequiredError) {
          setReconnect(true);
          onReconnectNeeded(true);
        }
        // Any other failure: keep showing the stored display name.
      });
    return () => {
      active = false;
    };
    // onReconnectNeeded is a state setter; its identity must not trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, profileId, idToken]);

  const name = info?.creator_nickname || displayName || "";
  return (
    <div className="mb-4 flex flex-col items-center gap-1" data-testid="tiktok-card-status">
      {info?.creator_avatar_url && (
        <img
          alt="TikTok avatar"
          src={info.creator_avatar_url}
          className="h-12 w-12 rounded-full object-cover"
        />
      )}
      {name && <p className="text-sm font-semibold text-gray-800">{name}</p>}
      {info?.creator_username && (
        <p className="text-xs text-gray-500">@{info.creator_username}</p>
      )}
      {reconnect && (
        <p role="alert" className="text-xs text-amber-700">
          Your TikTok connection has expired. Reconnect to keep posting.
        </p>
      )}
    </div>
  );
};

export default TikTokCardStatus;
